import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as TeamApi from "../../services/team.service"
import Swal from "sweetalert2";
import { SWAL_SETTINGS, THEME_COLORS } from "../../../../utils/Constants";
import { formateDateWithMonth, handleServerValidations, hasP, TrimText } from "../../../../utils/commonfunction";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import NoRowsOverlay from "../../common/NoRowsOverlay";
import Breadcrums from "../../common/breadcrumbs";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";

const Team = () => {

  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef();
  const [searchData, setSearchData] = useState("");
  const [statsupdate, setStatusUpdate] = useState("false");
  const [pageIndex] = useState(Number(params.pgno) - 1 || 0);
  const [perPage, setPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("Team"), url: "" },
  ];

  const onSelectionChanged = (event) => {
    if (event && event.api) {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map((node) => node.data);
      setSelectedRows(selectedData);
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedRows.length === 0) return;
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: `Are you sure you want to delete ${selectedRows.length} selected record(s)? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS?.ADMIN_PRIMARY || "#f1388b",
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while selected records are being deleted",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const deletePromises = selectedRows.map((row) => TeamApi.Delete(row.originalId));
        Promise.all(deletePromises)
          .then(() => {
            setSelectedRows([]);
            if (gridRef.current && gridRef.current.api) {
              gridRef.current.api.deselectAll();
            }
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: `${selectedRows.length} record(s) deleted successfully.`,
              ...SWAL_SETTINGS,
            });
            refetch();
          })
          .catch((error) => {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(error),
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };

  // ================= FETCH ALL DATA =================
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["players-list"],
    queryFn: () => TeamApi.List({
      limit: 1000,
      lastKey: null,
      filters: {},
      sorting: {}
    }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const viewfunction = (row) => {
    navigate(`/admin/team-management/team/view/${row.originalId}`);
  };

  const deleteFunction = (row) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This team will be soft-deleted and hidden from all lists. Match history will remain intact.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      customClass: "swal-wide",
      position: "center",
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        return TeamApi.Delete(row.originalId)
          .then(() => {
            return true;
          })
          .catch((err) => {
            const msg = err?.response?.data?.message || handleServerValidations(err) || "Something went wrong.";
            Swal.showValidationMessage(msg);
            return false;
          });
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({ icon: "success", text: "Team deleted successfully.", ...SWAL_SETTINGS });
        refetch();
      }
    });
  };

  // ================= COLUMNS =================
  const colDefs = useMemo(() => [
    {
      headerName: "",
      field: "checkbox",
      maxWidth: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
    },
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: t("name"),
      field: "name",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      tooltipField: "name",
      cellRenderer: (params) => {
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("label_club_name"),
      field: "clubAdminName",
      flex: 1,
      minWidth: 150,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        const clubAdminId = params.data?.clubAdminId;
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              cursor: clubAdminId ? "pointer" : "default",
            }}
            onClick={() => {
              if (clubAdminId) {
                navigate(`/admin/user-management/club-admins/view/${clubAdminId}`);
              }
            }}
          >
            {params.value || "N/A"}
          </div>
        );
      },
    },
    {
      headerName: t("created At"),
      field: "createdAt",
      flex: 1,
      minWidth: 140,
      maxWidth: 140,
      sortable: true,
      filter: true,
      tooltipField: "createdAt",
      cellRenderer: (params) => {
        const formattedDate = params.value
          ? new Date(params.value).toLocaleDateString("en-US", {
            month: "long",
            day: "2-digit",
            year: "numeric",
          })
          : "N/A";
        return (
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formattedDate}
          </div>
        );
      },
    },
    ...(hasP("team-view") ? [{
      headerName: t("list_heading_action"),
      maxWidth: 150,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              {hasP("team-view") && (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn view"
                    onClick={() => viewfunction(row)}
                  >
                    <i className="ri-eye-fill"></i>
                  </button>
                </Whisper>
              )}
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
              >
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => deleteFunction(row)}
                >
                  <i className="ri-delete-bin-fill"></i>
                </button>
              </Whisper>
            </div>
          </div>
        );
      },
    }] : []),
  ], [t, navigate]);

  // ================= ROW DATA =================
  const rowData = useMemo(() => {
    if (!data?.data?.list) return [];

    return data.data.list
      .map((row, i) => ({
        srNo: 0,
        originalId: row?.id,
        name: row?.name || "N/A",
        clubAdminName: row?.clubAdminName || "N/A",
        clubAdminId: row?.clubAdminId || null,
        createdAt: row?.createdAt || "N/A",
      }))
      .filter((row) =>
        searchData
          ? row.name.toLowerCase().includes(searchData.toLowerCase()) ||
          row?.clubAdminName?.toLowerCase().includes(searchData?.toLowerCase())
          : true
      );
  }, [data, searchData]);

  // ================= AG GRID CONFIG =================
  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    rowSelection: "multiple",
    onSelectionChanged: onSelectionChanged,
    getRowId: (params) => String(params.data.originalId),
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: perPage,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
    onPaginationChanged: (params) => {
      if (params.api) {
        const currentPage = params.api.paginationGetCurrentPage();
        const pageSize = params.api.paginationGetPageSize();
        setPerPage(pageSize);
      }
    },
  }), [rowData, colDefs, perPage]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="card custom-card overflow-hidden">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <h6 className="main-content-label mb-0">Team</h6>
                {selectedRows.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-flex align-items-center gap-1"
                    onClick={handleDeleteMultiple}
                  >
                    <i className="ri-delete-bin-line"></i> Delete Selected ({selectedRows.length})
                  </button>
                )}
              </div>
              <div className="d-flex align-items-center">
                <div style={{ width: 300 }} className="me-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("input_placeholder_search")}
                    value={searchData}
                    onChange={(e) => setSearchData(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>

                {/* date */}
                <div className="me-3">
                  <CustomProvider>
                    <DateRangePicker
                      style={{ width: "150px" }}
                      placeholder={t("input_placeholder_date")}
                      placement="bottomEnd"
                      onChange={(value) => {
                        if (!gridRef || !gridRef?.current || !gridRef?.current?.api) return;

                        if (!value || value?.length !== 2) {
                          gridRef?.current?.api?.setFilterModel(null);
                          gridRef?.current?.api?.onFilterChanged();
                          return;
                        }
                        const [start, end] = value;
                        gridRef?.current?.api?.setFilterModel({
                          createdAt: {
                            type: "inRange",
                            dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                            dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                          }
                        });
                        gridRef?.current?.api?.onFilterChanged();
                      }}
                    />
                  </CustomProvider>
                </div>
              </div>
            </div>
            {isLoading || isFetching ? (
              <Placeholder.Paragraph
                className="w-100 admin-placeholder"
                rowHeight={30}
                rowSpacing={5}
                active={true}
                rows={7}
              />
            ) : (
              <div className="ag-theme-alpine" style={{ width: '100%' }}>
                <AgGridReact {...getAgGridConfig} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Team;